import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const Container = styled.div`
  max-width: 1400px;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const GalleryContainer = styled.section`
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  min-height: 100vh;
  background: var(--background);
`;

export const GalleryHeader = styled.div`
  background: var(--surface);
  padding-top: 20px;
  margin-top: 75px;
  text-align: center;
  padding-bottom: 30px;

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }

  @media (max-width: 920px) {
    margin-top: 66.7px;
  }
`;

export const FilterButtons = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeInUp} 1s ease-out 0.3s both;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

export const FilterButton = styled.button`
  padding: 12px 25px;
  border: 2px solid
    ${(props) => (props.$active ? "var(--accent)" : "var(--accent-light)")};
  background: ${(props) =>
    props.$active ? "var(--accent)" : "var(--surface)"};
  color: ${(props) => (props.$active ? "var(--surface)" : "var(--accent)")};
  border-radius: 25px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: var(--shadow-soft);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 11px;
  }
`;

export const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  width: 100%;
  max-width: 1200px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

export const GalleryItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: var(--surface);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s ease;
  border: 2px solid var(--accent-light);
  cursor: pointer;
  box-shadow: var(--shadow-soft);
  animation: ${scaleIn} 0.6s ease forwards;
  animation-delay: ${(props) => props.$delay || 0}ms;
  opacity: 0;

  &:hover {
    /* transform: translateY(-12px) scale(1.02); */
    /* border-color: var(--accent);
    box-shadow: var(--shadow-medium); */
    background: rgba(124, 14, 51, 0.02);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--gradient-primary);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover::before {
    opacity: 0.1;
  }
`;

export const GalleryImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.$src});
  background-size: cover;
  background-position: center;
  position: relative;

  &.loading {
    background: linear-gradient(
      90deg,
      var(--accent-light) 0%,
      var(--accent) 50%,
      var(--accent-light) 100%
    );
    background-size: 200px 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`;

export const GalleryOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 25px;
  background: linear-gradient(transparent, rgba(44, 44, 44, 0.9));
  color: var(--surface);
  z-index: 2;
  transform: translateY(100%);
  transition: transform 0.3s ease;

  ${GalleryItem}:hover & {
    transform: translateY(0);
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  p {
    font-size: 14px;
    line-height: 1.4;
    opacity: 0.9;
  }

  .category {
    font-size: 12px;
    color: var(--accent-light);
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: 5px;
  }
`;

export const LoadMoreButton = styled.button`
  margin-top: 60px;
  padding: 15px 35px;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
  transition:
    color 0.3s ease,
    transform 0.3s ease,
    box-shadow 0.3s ease;
  box-shadow: var(--shadow-soft);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: var(--text-muted);

  h3 {
    font-size: 24px;
    margin-bottom: 15px;
    color: var(--text-secondary);
  }

  p {
    font-size: 16px;
    line-height: 1.6;
  }
`;

export const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(44, 44, 44, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

export const ModalContent = styled.div`
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: var(--shadow-medium);
  transform: ${(props) => (props.$isOpen ? "scale(1)" : "scale(0.8)")};
  transition: transform 0.3s ease;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(44, 44, 44, 0.8);
  color: var(--surface);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  z-index: 1001;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(44, 44, 44, 1);
    transform: scale(1.1);
  }
`;
